/**
 * Resolves brewery / taproom labels for Engage BeerCard display.
 */

/**
 * @param {string | undefined} breweryId
 * @param {Record<string, boolean> | null | undefined} groupedBreweryIds
 * @returns {boolean}
 */
export function isGroupedPrimaryBrewery(breweryId, groupedBreweryIds) {
  const normalizedId = typeof breweryId === 'string' ? breweryId.trim() : '';
  if (!normalizedId || !groupedBreweryIds) {
    return false;
  }
  return Boolean(groupedBreweryIds[normalizedId]);
}

/**
 * Labels for secondary taprooms (excludes primary breweryId).
 * @param {{ breweryId?: string, availableBreweryIds?: string[] }} beer
 * @param {Record<string, string> | null | undefined} breweryNameById
 * @returns {{ breweryId: string, label: string }[]}
 */
export function getSecondaryTaproomLabels(beer, breweryNameById) {
  const primaryId = typeof beer.breweryId === 'string' ? beer.breweryId.trim() : '';
  const availableIds = Array.isArray(beer.availableBreweryIds)
    ? beer.availableBreweryIds.filter((breweryId) => typeof breweryId === 'string' && breweryId.trim())
    : [];

  const seenIds = new Set();
  const rows = [];
  for (const breweryId of availableIds) {
    const normalizedId = breweryId.trim();
    if (!normalizedId || normalizedId === primaryId || seenIds.has(normalizedId)) {
      continue;
    }
    seenIds.add(normalizedId);
    const label = breweryNameById?.[normalizedId];
    if (label) {
      rows.push({ breweryId: normalizedId, label });
    }
  }

  return rows.sort((left, right) =>
    left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Labels for every taproom in availableBreweryIds (includes primary).
 * @param {{ breweryId?: string, availableBreweryIds?: string[] }} beer
 * @param {Record<string, string> | null | undefined} breweryNameById
 * @returns {{ breweryId: string, label: string }[]}
 */
export function getAllTaproomLocationLabels(beer, breweryNameById) {
  const primaryId = typeof beer.breweryId === 'string' ? beer.breweryId.trim() : '';
  const availableIds = Array.isArray(beer.availableBreweryIds)
    ? beer.availableBreweryIds.filter((breweryId) => typeof breweryId === 'string' && breweryId.trim())
    : [];
  const breweryIds =
    availableIds.length > 0
      ? availableIds
      : primaryId
        ? [primaryId]
        : [];

  const seenIds = new Set();
  const rows = [];
  for (const breweryId of breweryIds) {
    const normalizedId = breweryId.trim();
    if (!normalizedId || seenIds.has(normalizedId)) {
      continue;
    }
    seenIds.add(normalizedId);
    const label = breweryNameById?.[normalizedId];
    if (label) {
      rows.push({ breweryId: normalizedId, label });
    }
  }

  return rows.sort((left, right) =>
    left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
  );
}

/**
 * @param {{ breweryId?: string, breweryName?: string, availableBreweryIds?: string[] }} beer
 * @param {Record<string, string> | null | undefined} breweryNameById
 * @param {Record<string, boolean> | null | undefined} groupedBreweryIds
 * @returns {{ showBrandName: boolean, brandName: string | null, locationLabels: { breweryId: string, label: string }[] }}
 */
export function resolveBeerCardBreweryDisplay(beer, breweryNameById, groupedBreweryIds) {
  const useLocationLabelsOnly = isGroupedPrimaryBrewery(beer.breweryId, groupedBreweryIds);

  if (useLocationLabelsOnly) {
    return {
      showBrandName: false,
      brandName: null,
      locationLabels: getAllTaproomLocationLabels(beer, breweryNameById),
    };
  }

  const brandName = beer.breweryName || null;
  return {
    showBrandName: Boolean(brandName),
    brandName,
    locationLabels: getSecondaryTaproomLabels(beer, breweryNameById),
  };
}
